import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, LoaderCircle, Save } from "lucide-react";
import { useState } from "react";
import { type UseFormSetError, useForm } from "react-hook-form";
import { z } from "zod";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api";

// Espejo de la validación de servidor (factory/candado_dto.go): código obligatorio
// ≤60, descripción ≤500.
const candadoSchema = z.object({
  codigo_candado: z
    .string()
    .trim()
    .min(1, "El código es obligatorio")
    .max(60, "Máximo 60 caracteres"),
  descripcion: z.string().trim().max(500, "Máximo 500 caracteres"),
  activo: z.boolean(),
});

export type CandadoFormValues = z.infer<typeof candadoSchema>;

function aplicarError(
  err: unknown,
  setError: UseFormSetError<CandadoFormValues>,
  setFormError: (msg: string) => void,
): void {
  if (err instanceof ApiError) {
    if (err.code === "CODIGO_DUPLICADO") {
      setError("codigo_candado", { message: "Ya existe un candado con ese código." });
      return;
    }
    if (err.code === "VALIDACION" && err.fields) {
      if (err.fields.codigo_candado) {
        setError("codigo_candado", { message: err.fields.codigo_candado });
      }
      if (err.fields.descripcion) {
        setError("descripcion", { message: err.fields.descripcion });
      }
      return;
    }
    setFormError(err.message || "No se pudo guardar el candado.");
    return;
  }
  setFormError("No se pudo guardar el candado. Comprueba tu conexión.");
}

/**
 * CandadoForm es el formulario reutilizable de alta/edición de candados
 * (react-hook-form + zod). Mapea los errores del backend a errores por campo (código
 * duplicado, validación), replicando el patrón del maestro de áreas (#27).
 */
export function CandadoForm({
  defaultValues,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
}: {
  defaultValues: CandadoFormValues;
  submitLabel: string;
  pending: boolean;
  onSubmit: (values: CandadoFormValues) => Promise<void>;
  onCancel: () => void;
}) {
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<CandadoFormValues>({ resolver: zodResolver(candadoSchema), defaultValues });

  const submit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await onSubmit(values);
    } catch (err) {
      aplicarError(err, setError, setFormError);
    }
  });

  return (
    <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
      {formError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{formError}</span>
        </Alert>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="codigo_candado">Código</Label>
        <Input
          id="codigo_candado"
          autoComplete="off"
          aria-invalid={errors.codigo_candado ? true : undefined}
          {...register("codigo_candado")}
        />
        <FieldError>{errors.codigo_candado?.message}</FieldError>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="descripcion">Descripción (opcional)</Label>
        <Textarea
          id="descripcion"
          aria-invalid={errors.descripcion ? true : undefined}
          {...register("descripcion")}
        />
        <FieldError>{errors.descripcion?.message}</FieldError>
      </div>

      <label className="flex items-center gap-2.5 text-slate-700 text-sm">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-slate-300 text-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40"
          {...register("activo")}
        />
        Candado activo (disponible para su uso)
      </label>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          Cancelar
        </Button>
        <Button type="submit" disabled={pending}>
          {pending ? (
            <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : (
            <Save className="h-5 w-5" aria-hidden="true" />
          )}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
